import { TAU, circle } from '../paint';
import type { Ctx, Piece } from '../paint';

const OUT = 'rgba(4,6,18,.9)';

interface GiantInk {
  readonly c: string;
  readonly deep: string;
  readonly light: string;
  readonly skin: string;
  readonly ink: string;
  readonly iris: string;
  readonly band: string;
}

const GIANT_INKS: readonly [GiantInk, GiantInk, GiantInk, GiantInk] = [
  {
    c: '#ff7a3d',
    deep: '#d8381a',
    light: '#ffd46e',
    skin: '#ffc070',
    ink: '#4a1004',
    iris: '#c8250c',
    band: '#ff7a3d',
  },
  {
    c: '#4fb3ff',
    deep: '#1d5fb4',
    light: '#d2efff',
    skin: '#bfe6ff',
    ink: '#08234c',
    iris: '#1d5fb4',
    band: '#4fb3ff',
  },
  {
    c: '#e6f4ff',
    deep: '#86acd8',
    light: '#ffffff',
    skin: '#f2f8ff',
    ink: '#1e3360',
    iris: '#4f86c8',
    band: '#9cc4ee',
  },
  {
    c: '#e0a84a',
    deep: '#7a4c22',
    light: '#ffe0a0',
    skin: '#c99a58',
    ink: '#2a1606',
    iris: '#ffb23a',
    band: '#e0a84a',
  },
];
const [FIRE, WATER, AIR, EARTH] = GIANT_INKS;

export const FACES = [
  { size: 12.5, y: -18 },
  { size: 14.5, y: -40 },
  { size: 11, y: 0 },
  { size: 15, y: 0 },
] as const;
export const THIRD_EYE = 0.56;
// Where a phoenix wing joins the body, and the koi's tail.
export const WING_ROOT = { x: 6, y: -6 } as const;
export const KOI_TAIL_ROOT = 27;
export const RAY = 256;
export const RING_FLAME = 13;

type Points = readonly (readonly [number, number])[];

function fillOut(ctx: Ctx, fill: string | CanvasGradient | null, stroke: string | null, width = 1) {
  if (fill !== null) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke !== null) {
    ctx.lineWidth = width;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }
}

function shape(ctx: Ctx, points: Points, fill: string | null, stroke: string | null, width = 1) {
  ctx.beginPath();
  for (const [x, y] of points) {
    ctx.lineTo(x, y);
  }
  ctx.closePath();
  fillOut(ctx, fill, stroke, width);
}

function disc(ctx: Ctx, x: number, y: number, r: number, fill: string, stroke: string | null) {
  circle(ctx, r, x, y);
  fillOut(ctx, fill, stroke, 1.4);
}

function oval(ctx: Ctx, x: number, y: number, rx: number, ry: number, turn: number, fill: string) {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, turn, 0, TAU);
  fillOut(ctx, fill, null);
}

// A flame feather along +x, its tip hooked toward -y, in three nested layers.
function flame(ctx: Ctx, length: number, width: number, hook: number, ink: GiantInk): void {
  const layer = (wide: number, long: number, color: string, outlined: boolean) => {
    const steps = 14;
    ctx.beginPath();
    for (let side = -1; side <= 1; side += 2) {
      for (let step = 0; step <= steps; step += 1) {
        const share = side < 0 ? step / steps : 1 - step / steps;
        const x = share * length * long;
        const y = -hook * share ** 2.4 * length * long * 0.45;
        const half = width * wide * Math.sin(Math.PI * share ** 0.6) * (1 - 0.25 * share);
        ctx.lineTo(x, y + side * half);
      }
    }
    ctx.closePath();
    fillOut(ctx, color, outlined ? OUT : null, 1.1);
  };
  layer(1, 1, ink.deep, true);
  layer(0.6, 0.86, ink.c, false);
  layer(0.28, 0.68, ink.light, false);
}

function at(ctx: Ctx, x: number, y: number, turn: number, draw: () => void): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(turn);
  draw();
  ctx.restore();
}

function face(ctx: Ctx, s: number, ink: GiantInk): void {
  const line = Math.max(0.55, s * 0.07);
  for (const side of [-1, 1]) {
    at(ctx, side * s * 0.42, 0, -side * 0.14, () => {
      const w = s * 0.3;
      const h = s * 0.16;
      ctx.beginPath();
      ctx.moveTo(-w * 1.12, s * 0.02);
      ctx.quadraticCurveTo(0, -s * 0.36, w * 1.12, s * 0.02);
      ctx.quadraticCurveTo(0, -h * 2.2 - s * 0.02, -w * 1.12, s * 0.02);
      fillOut(ctx, ink.deep, null);
      ctx.beginPath();
      ctx.moveTo(-w * 1.12, s * 0.02);
      ctx.quadraticCurveTo(0, -s * 0.36, w * 1.12, s * 0.02);
      fillOut(ctx, null, ink.ink, line);
      ctx.beginPath();
      ctx.moveTo(-w, 0);
      ctx.quadraticCurveTo(0, s * 0.12, w, 0);
      fillOut(ctx, null, ink.ink, line * 1.3);
    });
    ctx.beginPath();
    ctx.moveTo(side * s * 0.1, -s * 0.3);
    ctx.quadraticCurveTo(side * s * 0.45, -s * 0.5, side * s * 0.82, -s * 0.24);
    fillOut(ctx, null, ink.ink, line);
    ctx.beginPath();
    for (let step = 0; step <= 16; step += 1) {
      const share = step / 16;
      const angle = side * (share * 1.6 * TAU) - Math.PI / 2;
      const r = s * 0.13 * (1 - 0.8 * share);
      ctx.lineTo(side * s * 0.6 + Math.cos(angle) * r, s * 0.36 + Math.sin(angle) * r);
    }
    fillOut(ctx, null, ink.ink, line * 0.8);
  }
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.12);
  ctx.quadraticCurveTo(-s * 0.1, s * 0.2, -s * 0.12, s * 0.3);
  ctx.quadraticCurveTo(0, s * 0.38, s * 0.12, s * 0.3);
  fillOut(ctx, null, ink.ink, line * 0.8);
  ctx.beginPath();
  ctx.moveTo(-s * 0.26, s * 0.56);
  ctx.quadraticCurveTo(-s * 0.12, s * 0.44, 0, s * 0.5);
  ctx.quadraticCurveTo(s * 0.12, s * 0.44, s * 0.26, s * 0.56);
  ctx.quadraticCurveTo(0, s * 0.84, -s * 0.26, s * 0.56);
  fillOut(ctx, ink.deep, ink.ink, line * 0.8);
  ctx.beginPath();
  ctx.moveTo(-s * 0.22, s * 0.57);
  ctx.lineTo(s * 0.22, s * 0.57);
  fillOut(ctx, null, ink.ink, line * 0.7);
  at(ctx, 0, -s * THIRD_EYE, 0, () => {
    const length = s * 0.2;
    ctx.beginPath();
    ctx.moveTo(0, -length);
    ctx.lineTo(0, length);
    fillOut(ctx, null, ink.ink, line);
    shape(
      ctx,
      [
        [0, -length - s * 0.16],
        [s * 0.06, -length - s * 0.08],
        [0, -length],
        [-s * 0.06, -length - s * 0.08],
      ],
      ink.light,
      ink.ink,
      line * 0.6,
    );
  });
}

// Both eyes open, painted over the shut ones and opened by stretching them from their middle line.
function eyes(index: number): (ctx: Ctx) => void {
  const ink = GIANT_INKS[index] ?? FIRE;
  const s = FACES[index]?.size ?? 12;
  return (ctx) => {
    const line = Math.max(0.55, s * 0.07);
    for (const side of [-1, 1]) {
      at(ctx, side * s * 0.42, 0, -side * 0.14, () => {
        const w = s * 0.3;
        const h = s * 0.16;
        const almond = () => {
          ctx.beginPath();
          ctx.moveTo(-w, 0);
          ctx.quadraticCurveTo(0, -h * 2.2, w, 0);
          ctx.quadraticCurveTo(0, h * 1.5, -w, 0);
        };
        almond();
        fillOut(ctx, '#fff4dc', null);
        ctx.save();
        almond();
        ctx.clip();
        disc(ctx, 0, h * 0.1, s * 0.13, ink.iris, null);
        disc(ctx, 0, h * 0.1, s * 0.06, '#0b0a18', null);
        disc(ctx, -s * 0.04, -h * 0.25, s * 0.03, '#ffffff', null);
        ctx.restore();
        almond();
        fillOut(ctx, null, ink.ink, line);
      });
    }
  };
}

function third(index: number): (ctx: Ctx) => void {
  const ink = GIANT_INKS[index] ?? FIRE;
  const s = FACES[index]?.size ?? 12;
  return (ctx) => {
    const line = Math.max(0.55, s * 0.07);
    const length = s * 0.2;
    const bulge = s * 0.11;
    ctx.beginPath();
    ctx.moveTo(0, -length);
    ctx.quadraticCurveTo(bulge * 2, 0, 0, length);
    ctx.quadraticCurveTo(-bulge * 2, 0, 0, -length);
    fillOut(ctx, '#fffbe8', ink.ink, line);
    disc(ctx, 0, 0, bulge * 0.7, ink.iris, null);
    circle(ctx, bulge * 0.7);
    fillOut(ctx, null, ink.ink, line * 0.5);
  };
}

function phoenix(ctx: Ctx): void {
  const plumes: Points = [
    [-0.62, 36],
    [0.62, 36],
    [-0.3, 44],
    [0.3, 44],
    [0, 52],
  ];
  for (const [turn, length] of plumes) {
    at(ctx, 0, 16, Math.PI / 2 + turn, () => {
      ctx.scale(1, turn < 0 ? -1 : 1);
      flame(ctx, length, 8, 0.45, FIRE);
    });
  }
  ctx.beginPath();
  ctx.ellipse(0, 4, 8.5, 18, 0, 0, TAU);
  fillOut(ctx, FIRE.c, OUT, 1.4);
  for (let row = 0; row < 3; row += 1) {
    ctx.beginPath();
    ctx.moveTo(-5, 4 + row * 6);
    ctx.lineTo(0, 8 + row * 6);
    ctx.lineTo(5, 4 + row * 6);
    fillOut(ctx, null, FIRE.light, 1.2);
  }
  for (const turn of [-0.5, 0, 0.5]) {
    at(ctx, 0, -28, -Math.PI / 2 + turn, () => {
      flame(ctx, turn === 0 ? 20 : 15, 4.5, 0, FIRE);
    });
  }
  disc(ctx, 0, FACES[0].y, 14, FIRE.skin, OUT);
  at(ctx, 0, FACES[0].y, 0, () => {
    face(ctx, FACES[0].size, FIRE);
  });
}

// The right wing, from its root at the left edge of the piece.
function phoenixWing(ctx: Ctx): void {
  ctx.translate(-36, 0);
  for (let feather = 4; feather >= 0; feather -= 1) {
    at(ctx, 0, feather * 3.2, -0.72 + feather * 0.3, () => {
      flame(ctx, 64 - feather * 7, 9.5 - feather * 0.7, 0.7, FIRE);
    });
  }
}

// Down the body from the head, as x, y and half width.
function koiBody(share: number): readonly [number, number, number] {
  const half = Math.max(
    4,
    27 * Math.sin(Math.PI * (0.1 + 0.9 * share)) ** 0.6 * (1 - 0.76 * share),
  );
  return [Math.sin(share * 2.4) * 6 * share * share, -50 + share * 80, half];
}

function koi(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    for (const [share, length, width] of [
      [0.3, 26, 10],
      [0.62, 16, 7],
    ] as const) {
      const [x, y, half] = koiBody(share);
      at(ctx, x + side * half * 0.8, y, side > 0 ? 0.75 : Math.PI - 0.75, () => {
        ctx.scale(1, side);
        flame(ctx, length, width, -0.4, WATER);
      });
    }
  }
  const outline = () => {
    ctx.beginPath();
    for (let step = 0; step <= 24; step += 1) {
      const [x, y, half] = koiBody(step / 24);
      ctx.lineTo(x - half, y);
    }
    for (let step = 24; step >= 0; step -= 1) {
      const [x, y, half] = koiBody(step / 24);
      ctx.lineTo(x + half, y);
    }
    ctx.closePath();
  };
  outline();
  const gradient = ctx.createLinearGradient(-26, 0, 26, 0);
  gradient.addColorStop(0, WATER.deep);
  gradient.addColorStop(0.5, WATER.c);
  gradient.addColorStop(1, WATER.deep);
  fillOut(ctx, gradient, null);
  ctx.save();
  outline();
  ctx.clip();
  const [ax, ay] = koiBody(0.4);
  const [bx, by] = koiBody(0.68);
  oval(ctx, ax - 9, ay, 10, 15, 0.4, '#eef9ff');
  oval(ctx, bx + 6, by, 7, 10, -0.3, '#eef9ff');
  for (let row = 0; row < 6; row += 1) {
    const [x, y, half] = koiBody(0.26 + row * 0.1);
    for (let column = -2; column <= 2; column += 1) {
      ctx.beginPath();
      ctx.arc(x + column * half * 0.4, y, half * 0.2, 0.15 * Math.PI, 0.85 * Math.PI);
      fillOut(ctx, null, WATER.deep, 0.8);
    }
  }
  ctx.restore();
  outline();
  fillOut(ctx, null, OUT, 1.5);
  for (const side of [-1, 1]) {
    ctx.beginPath();
    for (let step = 0; step <= 18; step += 1) {
      const share = step / 18;
      const angle = -Math.PI / 2 + side * (0.6 + share * 4.2);
      const r = 9 * (1 - 0.6 * share);
      ctx.lineTo(
        side * 11 + Math.cos(angle) * r * 0.9 + side * share * 6,
        -56 + Math.sin(angle) * r,
      );
    }
    fillOut(ctx, null, OUT, 3);
    fillOut(ctx, null, WATER.light, 1.5);
  }
  disc(ctx, 0, FACES[1].y, 17, WATER.skin, OUT);
  at(ctx, 0, FACES[1].y, 0, () => {
    face(ctx, FACES[1].size, WATER);
  });
}

interface SilkPoint {
  x: number;
  y: number;
  w: number;
}

function curl(points: SilkPoint[], turn: number, radius: number, turns: number, end: number): void {
  const last = points[points.length - 1];
  const before = points[points.length - 3];
  if (last === undefined || before === undefined) {
    return;
  }
  const length = Math.hypot(last.x - before.x, last.y - before.y) || 1;
  const ux = (last.x - before.x) / length;
  const uy = (last.y - before.y) / length;
  const cx = last.x - uy * turn * radius;
  const cy = last.y + ux * turn * radius;
  const start = Math.atan2(last.y - cy, last.x - cx);
  for (let step = 1; step <= 22; step += 1) {
    const share = step / 22;
    const angle = start + turn * share * turns * TAU;
    const r = radius * (1 - 0.72 * share);
    points.push({
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
      w: end * (1 - 0.8 * share),
    });
  }
}

function band(
  ctx: Ctx,
  points: readonly SilkPoint[],
  shift: number,
  size: number,
  fill: string,
  stroke: string,
  lines: readonly number[],
): void {
  const edges = points.map((point, index) => {
    const ahead = points[Math.min(index + 1, points.length - 1)] ?? point;
    const behind = points[Math.max(index - 1, 0)] ?? point;
    const length = Math.hypot(ahead.x - behind.x, ahead.y - behind.y) || 1;
    const nx = -(ahead.y - behind.y) / length;
    const ny = (ahead.x - behind.x) / length;
    const out = point.w * (shift + size / 2);
    const off = point.w * (shift - size / 2);
    return [
      point.x + nx * out,
      point.y + ny * out,
      point.x + nx * off,
      point.y + ny * off,
    ] as const;
  });
  ctx.beginPath();
  for (const [x, y] of edges) {
    ctx.lineTo(x, y);
  }
  for (let index = edges.length - 1; index >= 0; index -= 1) {
    const edge = edges[index];
    if (edge !== undefined) {
      ctx.lineTo(edge[2], edge[3]);
    }
  }
  ctx.closePath();
  fillOut(ctx, fill, OUT, 1.1);
  for (const share of lines) {
    ctx.beginPath();
    for (const [ox, oy, ix, iy] of edges) {
      ctx.lineTo(ox + (ix - ox) * share, oy + (iy - oy) * share);
    }
    fillOut(ctx, null, stroke, 0.6);
  }
}

// From the tail's root at the top middle of the piece.
function koiTail(ctx: Ctx): void {
  ctx.translate(0, -40);
  for (const side of [-1, 1]) {
    const points: SilkPoint[] = [];
    for (let step = 0; step <= 16; step += 1) {
      const share = step / 16;
      const angle = side * (0.42 + 0.25 * share);
      const away = share * 44;
      points.push({
        x: Math.sin(angle) * away,
        y: Math.cos(angle) * away,
        w: 4 + 11 * Math.sin(Math.PI * Math.min(1, share * 1.3)),
      });
    }
    curl(points, -side, 7, 1.1, 4);
    band(ctx, points, 0, 1, WATER.band, WATER.light, [0.25, 0.45]);
    band(ctx, points, side * 0.22, 0.5, WATER.light, '#ffffff', [0.35, 0.7]);
  }
}

function dragonHead(ctx: Ctx): void {
  const mane: GiantInk = { ...AIR, c: AIR.band };
  for (const side of [-1, 1]) {
    for (const [x, y, turn, length] of [
      [10, 2, 0.55, 22],
      [8, 8, 0.25, 18],
    ] as const) {
      at(ctx, side * x, y, Math.PI / 2 - side * turn, () => {
        ctx.scale(1, side);
        flame(ctx, length, 5.5, 0.6, mane);
      });
    }
    ctx.beginPath();
    ctx.moveTo(side * 7, -9);
    ctx.quadraticCurveTo(side * 16, -10, side * 19, 4);
    ctx.quadraticCurveTo(side * 20, 14, side * 26, 20);
    ctx.moveTo(side * 18.5, -1);
    ctx.quadraticCurveTo(side * 24, -4, side * 26, -10);
    fillOut(ctx, null, OUT, 4.4);
    fillOut(ctx, null, '#f6ecd2', 2.4);
    ctx.beginPath();
    for (let step = 0; step <= 22; step += 1) {
      const share = step / 22;
      ctx.lineTo(side * (9 + share * 16) + side * Math.sin(share * 6) * 3, 4 + share * 26);
    }
    fillOut(ctx, null, OUT, 2.6);
    fillOut(ctx, null, AIR.light, 1.2);
  }
  ctx.beginPath();
  ctx.moveTo(0, -15);
  ctx.quadraticCurveTo(13, -15, 13, -2);
  ctx.quadraticCurveTo(12, 10, 0, 15);
  ctx.quadraticCurveTo(-12, 10, -13, -2);
  ctx.quadraticCurveTo(-13, -15, 0, -15);
  const gradient = ctx.createLinearGradient(0, -15, 0, 15);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(1, AIR.band);
  fillOut(ctx, gradient, OUT, 1.5);
  face(ctx, FACES[2].size, AIR);
}

function fist(ctx: Ctx): void {
  const stone = (x: number, y: number, w: number, h: number, r: number) => {
    const gradient = ctx.createLinearGradient(x, y, x + w, y + h);
    gradient.addColorStop(0, '#e2b066');
    gradient.addColorStop(1, '#8a5a2a');
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    fillOut(ctx, gradient, OUT, 1.5);
  };
  stone(-19, 12, 38, 32, 8);
  ctx.beginPath();
  ctx.roundRect(-21, 16, 42, 8, 3);
  fillOut(ctx, EARTH.light, OUT, 1.2);
  for (let stud = 0; stud < 5; stud += 1) {
    disc(ctx, -14 + stud * 7, 20, 1.6, '#fff4cc', null);
  }
  stone(-31, -30, 62, 50, 14);
  for (let finger = 0; finger < 4; finger += 1) {
    stone(-31 + finger * 15.5, -40, 15.5, 18, 7);
  }
  ctx.beginPath();
  ctx.ellipse(-31, -4, 9, 15, -0.2, 0, TAU);
  fillOut(ctx, '#b07c40', OUT, 1.4);
  const cracks: readonly Points[] = [
    [
      [-19, 32],
      [-11, 36],
      [-8, 44],
    ],
    [
      [31, -12],
      [24, -4],
      [27, 6],
    ],
    [
      [-22, -20],
      [-15, -15],
    ],
  ];
  for (const crack of cracks) {
    ctx.beginPath();
    for (const [x, y] of crack) {
      ctx.lineTo(x, y);
    }
    fillOut(ctx, null, 'rgba(42,22,6,.75)', 1.1);
  }
  face(ctx, FACES[3].size, EARTH);
}

// From the ray's foot at the left edge of the piece.
function ray(ctx: Ctx): void {
  ctx.translate(-RAY / 2, 0);
  const gradient = ctx.createLinearGradient(0, 0, RAY, 0);
  gradient.addColorStop(0, 'rgb(255 255 255 / 1)');
  gradient.addColorStop(0.1, 'rgb(255 255 255 / 0.85)');
  gradient.addColorStop(1, 'rgb(255 255 255 / 0)');
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(RAY, -RAY * 0.05);
  ctx.lineTo(RAY, RAY * 0.05);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();
}

// From the flame's foot at the left edge of the piece.
function ringFlame(ctx: Ctx): void {
  ctx.translate(-RING_FLAME / 2, 0);
  flame(ctx, RING_FLAME, 4.2, 0.6, FIRE);
}

export const GIANT_PIECES = {
  phoenix: [96, 150, 50, phoenix],
  phoenixWing: [72, 110, 64, phoenixWing],
  koi: [116, 140, 60, koi],
  koiTail: [96, 80, 44, koiTail],
  dragonHead: [64, 72, 30, dragonHead],
  fist: [88, 100, 44, fist],
  eyes0: [32, 12, 6, eyes(0)],
  eyes1: [36, 14, 7, eyes(1)],
  eyes2: [28, 10, 5, eyes(2)],
  eyes3: [38, 14, 8, eyes(3)],
  third0: [8, 8, 3, third(0)],
  third1: [8, 10, 3, third(1)],
  third2: [6, 8, 3, third(2)],
  third3: [8, 10, 3, third(3)],
  ray: [RAY, 28, RAY, ray],
} satisfies Record<string, Piece>;

export const RING_FLAME_PIECES = {
  ringFlame: [RING_FLAME + 2, 18, RING_FLAME, ringFlame],
} satisfies Record<string, Piece>;
